/**
 * DrManiResults - the Dr. Mani reveal.
 *
 * Mirrors the v2 USCN results layout (two-column hero with a desktop matches
 * rail, spirit board, reading, quote rows, trust badges, secondary FAQ row,
 * sticky bundle footer) but with DMM-native classes (dmm-results-*,
 * dmm-product-card, dmm-spirit-board, dmm-quote-rows, dmm-bundle-cta, etc.)
 * from DrManiQuiz.css, so the page keeps its own naming convention and stays
 * decoupled from USCN CSS.
 *
 * All copy uses Dr. Mani product language (no "trees"/"citrus"): the matches are
 * the three pillars (Plant Super Boost / Crab Kelp & Amino Acids / Super Soil)
 * or the bundle, with dosing and a QUIZ10 bundle offer. The "Also Recommended"
 * add-ons render as full shoppable product cards (image + CTA to the PDP).
 */
import React, { useEffect, useRef, useState } from 'react';
import { BookOpen, Leaf, MapPin, ShieldCheck, Star, Truck } from 'lucide-react';
import type { DmmResolution } from '../../utils/drManiQuizLogic';
import type { RegimenAddOn, RegimenItem } from '../../data/drManiQuiz/types';
import {
  DMM_LINKS,
  DMM_OFFER,
  DMM_PRODUCTS,
  DMM_PRODUCT_FALLBACKS,
  DMM_ADDON_FALLBACKS,
} from '../../data/drManiQuiz/brand';
import { DMM_PRODUCT_BLURBS } from '../../data/drManiQuiz/sizing';
import {
  fetchFreshProductMetadata,
  getFreshProductMetadata,
  type FreshProductMetadata,
} from '../../services/shopifyImageService';
import type { ProductKey } from '../../data/drManiQuiz/types';
import DrManiCelebration from './DrManiCelebration';

interface DrManiResultsProps {
  resolution: DmmResolution;
  onBack: () => void;
}

/**
 * Static reassurance copy for the secondary row. These answer the objections
 * that stop a first order, so they sit beside the add-ons rather than in the
 * reading, where they would interrupt the identity beat.
 */
const DMM_RESULTS_FAQS = [
  {
    question: 'What if it does not work for me?',
    answer:
      'You get a 60-day keep-it guarantee. Try it on your worst plant. If you are not happy, tell us and we will make it right, and you keep the bottle either way.',
  },
  {
    question: 'How soon will I see a change?',
    answer:
      'Most gardeners see deeper green or new growth in two to three weeks. The roots move first and the leaves follow. Give it one full season and the difference stops being subtle.',
  },
  {
    question: 'Can I use too much and burn my plants?',
    answer:
      'No. This is not a salt fertilizer, so it will not burn roots and you cannot overdose it. Follow the dose on your plan, and know that a heavy hand will never cost you a plant.',
  },
];

/** Map DMM product keys to the Shopify handles shopifyImageService keys on. */
const PRODUCT_HANDLE: Record<ProductKey, string> = {
  plant_super_boost: 'plant-super-boost',
  crab_kelp_amino_acids: 'crab-kelp-amino-acids',
  super_soil: 'super-soil',
  the_bundle: 'the-bundle',
};

const DrManiResults: React.FC<DrManiResultsProps> = ({ resolution, onBack }) => {
  const [metadata, setMetadata] = useState<Record<string, FreshProductMetadata>>({});
  const [shareMessage, setShareMessage] = useState('');
  const offerBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    fetchFreshProductMetadata().then((md) => {
      if (active) setMetadata(md);
    });
    return () => {
      active = false;
    };
  }, []);

  /**
   * Publish the sticky offer bar's height so the sticky FAQ can park below it.
   * The bar's copy rewraps with viewport width, so a hardcoded offset would let
   * the bar cover the FAQ at some widths and leave a gap at others.
   */
  useEffect(() => {
    const bar = offerBarRef.current;
    const grid = bar?.closest<HTMLElement>('.dmm-results-grid');
    if (!bar || !grid) return;

    const publishHeight = () => {
      grid.style.setProperty('--dmm-offer-bar-height', `${Math.round(bar.getBoundingClientRect().height)}px`);
    };

    publishHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', publishHeight);
      return () => window.removeEventListener('resize', publishHeight);
    }

    const observer = new ResizeObserver(publishHeight);
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);

  const {
    archetype,
    profile,
    needDiagnosis,
    painVillains,
    segmentMorsel,
    segmentReview,
    regimen,
    keyInsight,
  } = resolution;

  const shareUrl = typeof window !== 'undefined' ? window.location.href : 'https://drmanismagic.com/pages/quiz';
  // Why this matters: share the aspirational identity (archetype), never the garden diagnosis.
  const shareText = `I'm ${archetype.name}. Take the Dr. Mani gardener quiz: ${shareUrl}`;

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "Dr. Mani's Magic Quiz", text: shareText, url: shareUrl });
        setShareMessage('Shared.');
        return;
      }
      await navigator.clipboard.writeText(shareText);
      setShareMessage('Share text copied.');
    } catch {
      setShareMessage('Share unavailable on this device.');
    }
  };

  const openBundle = () =>
    window.open(DMM_LINKS.bundle, '_blank', 'noopener,noreferrer');

  // Resolve image + rating + reviews for a regimen product, with hardcoded fallback.
  const metaFor = (key: ProductKey) => {
    const live = getFreshProductMetadata(PRODUCT_HANDLE[key], metadata);
    const fallback = DMM_PRODUCT_FALLBACKS[PRODUCT_HANDLE[key]];
    const imageUrl = live?.imageUrl || fallback?.imageUrl || '';
    const rating = live?.rating ?? fallback?.rating ?? null;
    const reviews = live?.reviews ?? fallback?.reviews ?? null;
    return { imageUrl, rating, reviews };
  };

  // Add-ons: image + reviews come from the live sync by handle, falling back to
  // DMM_ADDON_FALLBACKS (verified CDN images + real review counts) so the
  // "Also Recommended" cards never render with a blank placeholder.
  const metaForAddon = (handle: string) => {
    const live = getFreshProductMetadata(handle, metadata);
    const fallback = DMM_ADDON_FALLBACKS[handle];
    const imageUrl = live?.imageUrl || fallback?.imageUrl || '';
    const rating = live?.rating ?? fallback?.rating ?? null;
    const reviews = live?.reviews ?? fallback?.reviews ?? null;
    return { imageUrl, rating, reviews };
  };

  const topMatches = regimen.items;
  const spiritProduct = topMatches[0];
  const isBundleRec = topMatches.length === 1 && topMatches[0]?.key === 'the_bundle';
  const bundleItem = isBundleRec ? topMatches[0] : null;
  const bundlePillars = regimen.bundlePillars ?? [];

  const renderStars = (rating: number | null) => {
    const filled = rating ? Math.round(rating) : 0;
    return (
      // Why this matters: the group exposes one useful rating label while its individual stars stay decorative.
      <div
        className="dmm-product-stars"
        role="img"
        aria-label={rating ? `${rating.toFixed(2)} out of 5` : 'No rating'}
      >
        {[1, 2, 3, 4, 5].map((s) => (
          <Star
            key={s}
            className={`dmm-star ${s <= filled ? 'filled' : ''}`}
            size={16}
            strokeWidth={1.8}
            fill={s <= filled ? 'currentColor' : 'none'}
            aria-hidden="true"
          />
        ))}
      </div>
    );
  };

  const renderProductCard = (item: (typeof topMatches)[number], rank: number) => {
    const meta = metaFor(item.key);
    const imageUrl = item.variantImageUrl || meta.imageUrl;
    const { rating, reviews } = meta;
    const showRankBadge = rank >= 1 && rank <= 3;
    const showRoleBadge = rank === 0;
    return (
      <div className="dmm-product-card" key={item.key}>
        {showRankBadge && (
          <div className={`dmm-product-badge dmm-product-badge-${rank}`}>
            {rank === 1 ? '#1 Match' : rank === 2 ? '#2 Match' : '#3 Match'}
          </div>
        )}
        {showRoleBadge && (
          <div className="dmm-product-badge dmm-product-badge-role">✓ {item.role}</div>
        )}
        <div className="dmm-product-image-container">
          {imageUrl ? (
            <img src={imageUrl} alt={item.name} className="dmm-product-image" loading="lazy" />
          ) : (
            <div className="dmm-product-image dmm-product-image-placeholder" aria-hidden="true" />
          )}
        </div>
        <div className="dmm-product-content">
          <h3 className="dmm-product-name">{item.name}</h3>
          {/* UX-2: the match line connecting this card to their answers. */}
          {item.becauseLine && <p className="dmm-product-because">{item.becauseLine}</p>}
          <div className="dmm-product-reviews">
            {renderStars(rating)}
            {rating && <span className="dmm-product-rating">{rating.toFixed(2)}/5</span>}
            {reviews != null && reviews > 0 && (
              <span className="dmm-product-review-count">{reviews} reviews</span>
            )}
          </div>
          <p className="dmm-product-why">{item.snippet}</p>
          {item.sizing && (
            <div className="dmm-product-sizing">
              <p><strong>Size:</strong> {item.sizing.sizeLabel}</p>
              <p><strong>Quantity:</strong> {item.sizing.quantity}</p>
              <p><strong>Cadence:</strong> {item.sizing.cadence}</p>
              <p><strong>Lasts:</strong> {item.sizing.duration}</p>
              <p className="dmm-product-description">{item.dose}</p>
              {rank === 1 && <p className="dmm-product-card-copy">{item.sizing.cardCopy}</p>}
              <p className="dmm-subscription-bridge">{item.sizing.subscriptionBridge}</p>
            </div>
          )}
          {!item.sizing && item.dose && <p className="dmm-product-description">{item.dose}</p>}
          <div className="dmm-product-tags">
            <span className="dmm-product-tag">✓ {item.role}</span>
            {item.key !== 'the_bundle' && <span className="dmm-product-tag">✓ Won't burn, can't overdose</span>}
          </div>
          <div className="dmm-product-footer">
            <a href={item.productUrl} target="_blank" rel="noopener noreferrer" className="dmm-product-btn">
              Get {item.name}
            </a>
          </div>
        </div>
      </div>
    );
  };

  const renderAddOnCard = (addOn: RegimenAddOn) => {
    const { imageUrl, rating, reviews } = metaForAddon(addOn.handle);
    return (
      <div className="dmm-product-card" key={addOn.key}>
        <div className="dmm-product-image-container">
          {imageUrl ? (
            <img src={imageUrl} alt={addOn.name} className="dmm-product-image" loading="lazy" />
          ) : (
            // Why this matters: the neutral visual needs image semantics for its accessible name to be announced.
            <div
              className="dmm-product-image dmm-product-image-neutral"
              role="img"
              aria-label={`${addOn.name} nature illustration`}
            >
              <Leaf size={42} strokeWidth={1.5} aria-hidden="true" />
              <span>{addOn.name}</span>
            </div>
          )}
        </div>
        <div className="dmm-product-content">
          <h3 className="dmm-product-name">{addOn.name}</h3>
          <div className="dmm-product-reviews">
            {rating != null && renderStars(rating)}
            {rating && <span className="dmm-product-rating">{rating.toFixed(2)}/5</span>}
            {reviews != null && reviews > 0 && (
              <span className="dmm-product-review-count">{reviews} reviews</span>
            )}
          </div>
          <p className="dmm-product-description">{addOn.note}</p>
          <div className="dmm-product-footer">
            <a href={addOn.productUrl} target="_blank" rel="noopener noreferrer" className="dmm-product-btn">
              Get {addOn.name}
            </a>
          </div>
        </div>
      </div>
    );
  };

  const renderBundleMatches = (scope: string, titleId?: string) => {
    const displayBundle: RegimenItem = bundleItem || {
      key: 'the_bundle',
      name: DMM_PRODUCTS.the_bundle.name,
      role: DMM_PRODUCTS.the_bundle.role,
      snippet: DMM_PRODUCT_BLURBS.the_bundle,
      dose: '',
      productUrl: DMM_PRODUCTS.the_bundle.url,
    };

    return (
      <div className={`dmm-matches ${scope}`}>
        <div className="dmm-matches-header dmm-container">
          <h2 id={titleId} className="dmm-section-title">{displayBundle.name}</h2>
          <span className="dmm-matches-subtitle">The smart buy for the whole garden.</span>
        </div>

        <div className="dmm-bundle-rec">
          <div className="dmm-bundle-rec-header">
            <div className="dmm-product-badge dmm-product-badge-1">The Bundle</div>
            <h3 className="dmm-bundle-rec-title">{displayBundle.name}</h3>
            {/* UX-2: the bundle hero carries its stacked because line. */}
            {displayBundle.becauseLine && (
              <p className="dmm-product-because">{displayBundle.becauseLine}</p>
            )}
            <p className="dmm-bundle-rec-subtitle">The life, the food, and the home. The whole system at once.</p>
            <p className="dmm-bundle-rec-description">{displayBundle.snippet}</p>
            <div className="dmm-product-tags">
              <span className="dmm-product-tag">✓ {displayBundle.role}</span>
            </div>
            <div className="dmm-bundle-rec-cta">
              <button type="button" className="dmm-product-btn" onClick={openBundle}>
                Get {displayBundle.name}
              </button>
            </div>
          </div>

          <div className="dmm-bundle-rec-pillars">
            <p className="dmm-bundle-rec-pillars-label">What's inside your bundle</p>
            <div className="dmm-matches-grid">
              {bundlePillars.map((item) => renderProductCard(item, 0))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * The matches block renders twice: once in the desktop right rail and once
   * inline for mobile. Only one is ever visible, but both sit in the DOM, so the
   * heading id has to be passed in rather than hardcoded or it would duplicate.
   */
  const renderTopMatches = (scope: string, titleId: string) => {
    if (isBundleRec) return renderBundleMatches(scope, titleId);

    return (
      <div className={`dmm-matches ${scope}`}>
        <div className="dmm-matches-header dmm-container">
          <h2 id={titleId} className="dmm-section-title">Your Dr. Mani Matches</h2>
          <span className="dmm-matches-subtitle">
            {spiritProduct ? `${spiritProduct.name} fits your garden especially well.` : ''}
          </span>
        </div>
        <div className="dmm-matches-grid">
          {topMatches.map((item, index) => renderProductCard(item, index + 1))}
        </div>
      </div>
    );
  };

  const renderSegmentProof = () => {
    if (!segmentMorsel && !segmentReview) return null;

    return (
      <section className="dmm-results-proof" aria-labelledby="dmm-proof-title">
        <h2 id="dmm-proof-title" className="dmm-section-title">
          Gardeners like you
        </h2>
        {segmentMorsel && <p>{segmentMorsel}</p>}
        {segmentReview && (
          <figure className="dmm-segment-review">
            <p className="dmm-segment-review-setup">{segmentReview.setup}</p>
            <blockquote>“{segmentReview.quote}”</blockquote>
            <figcaption>
              {segmentReview.reviewer}, {segmentReview.product}, {segmentReview.verification}
            </figcaption>
          </figure>
        )}
        <a href={DMM_LINKS.reviews} target="_blank" rel="noopener noreferrer">
          Read real reviews from Dr. Mani gardeners
        </a>
      </section>
    );
  };

  const renderOfferText = () => (
    <>
      <span>
        Use code <strong>{DMM_OFFER.quiz_code}</strong> for another 10 percent off your first order. It
        stacks with the bundle savings, free shipping over {DMM_OFFER.free_shipping_threshold}, and the{' '}
        {DMM_OFFER.guarantee} guarantee.
      </span>
      <span className="dmm-offer-deadline">{DMM_OFFER.deadline}</span>
    </>
  );

  const renderOfferLine = () => (
    <span className="dmm-results-offer-line">
      {renderOfferText()}
    </span>
  );

  const renderOfferBar = () => (
    <div className="dmm-offer-banner">
      <span className="dmm-offer-banner-text">
        {renderOfferText()}
      </span>
    </div>
  );

  return (
    <div className="dmm-results-grid">
      {/* UX-7: colorful confetti finale. Why this matters: this labels the actual animation and cleanup path. */}
      <DrManiCelebration />
      <div ref={offerBarRef} className="dmm-results-offer-top dmm-container">{renderOfferBar()}</div>
      <div className="dmm-results-hero">
        <div className="dmm-results-hero-grid dmm-container">
          <div className="dmm-results-hero-column">
            <div className="dmm-results-content">
              <p className="dmm-results-kicker">Your Dr. Mani Reading</p>
              {/* Why this matters: the identity headline names the gardener, never the soil diagnosis. */}
              <h1 className="dmm-results-title">
                You are <span className="dmm-identity-name">{archetype.name}</span>.
              </h1>
              {/* The sub-headline and description diagnose the garden, so they sit
                  under the identity line rather than replacing it. */}
              <h2 className="dmm-results-headline">{profile.headline}</h2>
              <p className="dmm-results-description">{profile.description}</p>

              <div className="dmm-spirit-board">
                <div className="dmm-spirit-board-row dmm-spirit-board-title">
                  <div className="dmm-archetype-badge">
                    {/* Why this matters: the visible flourish is decorative, so screen readers should announce only the archetype name. */}
                    <span className="dmm-archetype-icon" aria-hidden="true">✨</span>
                    <span className="dmm-archetype-name">{archetype.name}</span>
                  </div>
                  <button className="dmm-btn dmm-btn-secondary dmm-share-btn" onClick={handleShare}>
                    Share My Result
                  </button>
                </div>
                {spiritProduct && (
                  <div className="dmm-spirit-board-row dmm-spirit-inline">
                    <small className="dmm-spirit-label">Your Spirit Product</small>
                    <span className="dmm-spirit-name">{spiritProduct.name}</span>
                  </div>
                )}
              </div>

              {shareMessage && <p className="dmm-share-message">{shareMessage}</p>}

              <div className="dmm-reading dmm-rte">
                {archetype.core_reading_lines.map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </div>

              {/* Phones get the matches here because the right rail collapses away. */}
              <div className="dmm-for-mobile">
                {renderTopMatches('dmm-plan', 'dmm-plan-title-mobile')}
              </div>

              <div className="dmm-quote-rows">
                <div className="dmm-quote-row dmm-rte">
                  <small className="dmm-quote-label">Your Superpower</small>
                  <span className="dmm-quote-summary"><b><i>{archetype.superpower}</i></b></span>
                </div>
                <div className="dmm-quote-row dmm-rte">
                  <small className="dmm-quote-label">What you are really after</small>
                  <span className="dmm-quote-summary"><b><i>{keyInsight.key_insight_lines.join(' ')}</i></b></span>
                </div>
                <div className="dmm-quote-row dmm-rte">
                  <small className="dmm-quote-label">Worth Remembering</small>
                  <span className="dmm-quote-summary"><b><i>{archetype.blind_spot}</i></b></span>
                </div>
              </div>

              <section className="dmm-results-diagnosis" aria-labelledby="dmm-diagnosis-title">
                <div className="dmm-garden-diagnosis">
                  <small className="dmm-garden-diagnosis-label">Your garden&apos;s diagnosis</small>
                  <h2 id="dmm-diagnosis-title" className="dmm-garden-diagnosis-name">{profile.name}</h2>
                </div>
                <div className="dmm-faq-row">
                  <h3>What your garden was missing</h3>
                  <p>{needDiagnosis}</p>
                </div>
                {painVillains.length > 0 && (
                  <div className="dmm-faq-row">
                    <h3>Your frustrations, explained</h3>
                    {painVillains.map((p) => (
                      <p key={p.key}>{p.text}</p>
                    ))}
                  </div>
                )}
              </section>

              {renderSegmentProof()}

              <section className="dmm-results-plan" aria-label="Your Dr. Mani plan">
                {/* When the bundle is the recommendation it already renders as the
                    matches block, so offering it again here would repeat it. */}
                {!isBundleRec && renderBundleMatches('dmm-bundle-offer', 'dmm-bundle-offer-title')}
                <p className="dmm-generosity-line">{regimen.generosityLine}</p>
              </section>

              <section className="dmm-trust-badges" aria-label="Dr. Mani trust details">
                <div className="dmm-trust-badge">
                  <BookOpen className="dmm-trust-icon" size={18} aria-hidden="true" />
                  <span>Free Field Guide</span>
                </div>
                <div className="dmm-trust-badge">
                  <ShieldCheck className="dmm-trust-icon" size={18} aria-hidden="true" />
                  <span>{DMM_OFFER.guarantee} guarantee</span>
                </div>
                <div className="dmm-trust-badge">
                  <Star className="dmm-trust-icon" size={18} aria-hidden="true" />
                  <span>3,986 reviews at 4.9 stars</span>
                </div>
                <div className="dmm-trust-badge">
                  <Truck className="dmm-trust-icon" size={18} aria-hidden="true" />
                  <span>Free shipping over {DMM_OFFER.free_shipping_threshold}</span>
                </div>
                <div className="dmm-trust-badge">
                  <MapPin className="dmm-trust-icon" size={18} aria-hidden="true" />
                  <span>Made by one Texas family</span>
                </div>
              </section>

              <div className="dmm-results-cta">
                <div className="dmm-results-offer">{renderOfferLine()}</div>
              </div>

              <div className="dmm-results-footer">
                <button className="dmm-btn dmm-btn-back" onClick={onBack}>
                  ← Retake Quiz
                </button>
                <a
                  href="https://drmanismagic.com/collections/all"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dmm-btn dmm-btn-secondary"
                >
                  Browse All Products
                </a>
              </div>

              {/* Last in the column on purpose: the product rail usually outruns
                  the reading, and a sticky block with nothing after it pins
                  through that leftover space without covering its own siblings. */}
              <div className="dmm-faq-rows">
                {DMM_RESULTS_FAQS.map((faq) => (
                  <div className="dmm-faq-row" key={faq.question}>
                    <h3>{faq.question}</h3>
                    <p>{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="dmm-results-hero-column">
            <div className="dmm-matches-rail dmm-for-desktop">
              {renderTopMatches('dmm-plan', 'dmm-plan-title-desktop')}
            </div>
          </div>
        </div>
      </div>

      {regimen.addOns.length > 0 && (
        <section
          className="dmm-results-section dmm-results-secondary"
          aria-labelledby="dmm-accessories-title"
        >
          <div className="dmm-container">
            <div className="dmm-matches-header">
              <h2 id="dmm-accessories-title" className="dmm-section-title">Also Recommended</h2>
              <span className="dmm-matches-subtitle">A few extras that pair with your plan.</span>
            </div>
            <div className="dmm-matches-grid">
              {regimen.addOns.map((a) => renderAddOnCard(a))}
            </div>
          </div>
        </section>
      )}

      <div className="dmm-cta-sticky dmm-container">
        <div className="dmm-bundle-cta">
          <div className="dmm-bundle-savings">
            <span className="dmm-bundle-discount"><i>Use code {DMM_OFFER.quiz_code}</i></span>
            <div className="dmm-bundle-pricing">
              <h3 className="dmm-bundle-title">The Dr. Mani&apos;s Magic Bundle</h3>
            </div>
          </div>
          <div className="dmm-bundle-footer">
            <div className="dmm-bundle-prices">
              <div className="dmm-bundle-prices-column">
                <span className="dmm-sticky-code">Use code {DMM_OFFER.quiz_code}</span>
                <span className="dmm-bundle-price">Free shipping over {DMM_OFFER.free_shipping_threshold}</span>
                <span className="dmm-bundle-deadline">{DMM_OFFER.deadline}</span>
              </div>
            </div>
            <button className="dmm-bundle-btn" onClick={openBundle}>
              Get The Dr. Mani&apos;s Magic Bundle
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrManiResults;
